import { useState } from "react";
import type { TemplateView } from "@/types/api";
import CategoryBrowser from "./CategoryBrowser";
import AiItemGenerator from "./AiItemGenerator";
import ItemListEditor from "./ItemListEditor";

interface ItemEntry {
  name: string;
  description?: string;
}

type SubState =
  | { mode: "browsing" }
  | { mode: "generating" }
  | { mode: "editing"; templateName?: string; templateId?: string };

interface StepItemsProps {
  items: ItemEntry[];
  onItemsChange: (items: ItemEntry[]) => void;
  onCategorySelected: (categoryName: string | undefined) => void;
  onTemplateSelected?: (templateId: string | undefined) => void;
}

export default function StepItems({
  items,
  onItemsChange,
  onCategorySelected,
  onTemplateSelected,
}: StepItemsProps) {
  const [subState, setSubState] = useState<SubState>(
    items.length > 0 ? { mode: "editing" } : { mode: "browsing" },
  );

  const handleTemplateSelect = (template: TemplateView) => {
    onItemsChange(template.items);
    onCategorySelected(template.category);
    onTemplateSelected?.(template.templateId);
    setSubState({
      mode: "editing",
      templateName: template.name,
      templateId: template.templateId,
    });
  };

  const handleCreateCustom = () => {
    onItemsChange([]);
    onCategorySelected(undefined);
    onTemplateSelected?.(undefined);
    setSubState({ mode: "editing" });
  };

  const handleGenerateWithAi = () => {
    setSubState({ mode: "generating" });
  };

  const handleAiItemsGenerated = (
    generatedItems: { name: string; description?: string }[],
  ) => {
    onItemsChange(generatedItems);
    onCategorySelected("AI Generated");
    onTemplateSelected?.(undefined);
    setSubState({
      mode: "editing",
      templateName: "AI Generated",
    });
  };

  const handleChangeTemplate = () => {
    onTemplateSelected?.(undefined);
    setSubState({ mode: "browsing" });
  };

  if (subState.mode === "browsing") {
    return (
      <div>
        <h2 className="font-display text-xl font-bold text-text">
          Choose Your Items
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          Pick a template or create your own list
        </p>
        <div className="mt-4">
          <CategoryBrowser
            onSelectTemplate={handleTemplateSelect}
            onCreateCustom={handleCreateCustom}
            onGenerateWithAi={handleGenerateWithAi}
          />
        </div>
      </div>
    );
  }

  if (subState.mode === "generating") {
    return (
      <AiItemGenerator
        onItemsGenerated={handleAiItemsGenerated}
        onBack={() => setSubState({ mode: "browsing" })}
      />
    );
  }

  // editing mode
  const isCustom = !subState.templateName;

  return (
    <div>
      {isCustom ? (
        <>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-text">
              Custom Item List
            </h2>
            <button
              type="button"
              onClick={handleChangeTemplate}
              className="text-sm font-medium text-primary hover:underline"
            >
              Use a template
            </button>
          </div>
          <p className="mt-1 text-sm text-text-secondary">
            Type an item name and press Enter to add it
          </p>
        </>
      ) : (
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-text">
            {subState.templateName}
          </h2>
          <button
            type="button"
            onClick={handleChangeTemplate}
            className="text-sm font-medium text-primary hover:underline"
          >
            Change
          </button>
        </div>
      )}
      <div className="mt-4">
        <ItemListEditor
          items={items}
          onChange={onItemsChange}
          mode={isCustom ? "custom" : "review"}
        />
      </div>
    </div>
  );
}
